import { DateTime } from 'luxon';

export interface PassengerExpanded {
  type: 'non-rev' | 'rev-stby' | 'other';
  isSeatAssigned: boolean;
  priority: string;
  lastName: string;
  firstName: string;
  seat?: string;
  rolloverFlight?: string;
  comment?: string;
  isListedCabinJs: boolean;
  isAssignedCabinJs: boolean;
  isListedFdJs: boolean;
  isAssignedFdJs: boolean;
}

export class StandbyListView {
  private _listFilter: 'all' | 'stby' | 'nonrev';

  timestamp: DateTime;
  list: PassengerExpanded[];
  rawText: string;

  constructor(
    text: string,
    listFilter: 'all' | 'stby' | 'nonrev',
    timestamp: string
  ) {
    this.rawText = text;
    this._listFilter = listFilter;
    this.list = this.parseStandbyList();
    this.timestamp = DateTime.fromISO(timestamp, { zone: 'utc' });
  }

  // eslint-disable-next-line complexity
  private parseStandbyList(): PassengerExpanded[] {
    /* Example text:

      AA  2357 24FEB   DEN C34  1047A  321   PRIORITY LIST
      T  1 -UPG2  GRINER     WILLI      L R DFW     18F  NB
      T  2 -UPG2  WILKE      NIELS      G R DFW     29A   1
      T  3 -UPG2  GARCIA     LINA       V R DFW     18D  NB
      T 39 -RV    DAVIS      JOSHU      B   DFW     33C  NB
      T 40 -D2    ANDERSON   KELLE      J   DFW      5X  NB CJ
      T 41 -D2    SMAT       JAMES      Y   DFW     26D   1
      T 42 -D2    ANDERSON   GEORG      J   DFW      6X  NB CJ
                1         2         3         4         5
      012345678901234567890123456789012345678901234567890123456789

      Columns are fixed, so parse by column index.
    */

    const seatRe = /\d{1,2}[A-Z]{1}/;
    const flightNumRe = /\d{4}/;
    const list: PassengerExpanded[] = [];

    for (const [index, line] of this.rawText.split('\n').entries()) {
      // Skip first line.
      if (index === 0) {
        continue;
      }

      let isSeatAssigned = false;

      // If dash (-) precedes priority code, it means seat assigned.
      if (line.slice(5, 6) === '-') {
        isSeatAssigned = true;
      }

      // Priority code: UPG2, RV, D2, etc.
      const priority = line.slice(6, 10).trim();

      // Name.
      const lastName = line.slice(12, 23).trim();
      const firstName = line.slice(23, 28).trim();

      // Seat or rollover flight number.
      const seatOrFlight = line.slice(45, 49).trim();
      let seat: string | undefined;
      let rolloverFlight: string | undefined;

      if (seatRe.test(seatOrFlight)) {
        seat = seatOrFlight;
        if (seat) {
          isSeatAssigned = true;
        }
      } else if (flightNumRe.test(seatOrFlight)) {
        rolloverFlight = seatOrFlight;
      }

      // Comments: NO ANS, CJ, FDJ AA, etc.
      let comment: string | undefined;
      if (line.length > 54) {
        comment = line.slice(54).trim();
      }

      // Id passenger type.
      let type: 'other' | 'non-rev' | 'rev-stby' = 'other';

      if (
        priority.startsWith('D1') ||
        priority.startsWith('D2') ||
        priority.startsWith('D3') ||
        priority.startsWith('D6') ||
        priority.startsWith('D7') ||
        priority.startsWith('DP') ||
        priority.startsWith('AAC') ||
        priority.startsWith('ZED') ||
        priority.startsWith('ONE')
      ) {
        type = 'non-rev';
      } else if (
        priority.startsWith('DSR') ||
        priority.startsWith('DG') ||
        priority.startsWith('RI') ||
        priority.startsWith('RV')
      ) {
        type = 'rev-stby';
      }

      // Id flight deck jumpseaters.
      let isListedFdJs = false;
      let isAssignedFdJs = false;
      if (
        (comment?.includes('FDJ') && type === 'non-rev') ||
        priority === 'D2WP' ||
        priority === 'D2WA' ||
        priority === 'D6UJ'
      ) {
        isListedFdJs = true;

        if (seat?.includes('W')) {
          isAssignedFdJs = true;
        }
      }

      // Id cabin jumpseaters.
      let isListedCabinJs = false;
      let isAssignedCabinJs = false;
      if (comment?.includes('CJ') || priority === 'D7U') {
        isListedCabinJs = true;

        if (seat?.includes('X')) {
          isAssignedCabinJs = true;
        }
      }

      list.push({
        type,
        isSeatAssigned,
        priority,
        lastName,
        firstName,
        seat,
        rolloverFlight,
        comment,
        isListedCabinJs,
        isAssignedCabinJs,
        isListedFdJs,
        isAssignedFdJs,
      });
    }

    /* eslint-disable indent */
    // Filter revenue standbys depending on nonRevsOnly setting.
    return list.filter((item) => {
      if (item.firstName === '') {
        return false;
      }

      switch (this._listFilter) {
        case 'all':
          return true;
        case 'stby':
          return item.type === 'rev-stby' || item.type === 'non-rev';
        case 'nonrev':
          return item.type === 'non-rev';
        default:
          return true;
      }
    });
  }
}
